import { useState, useEffect } from 'react'

import { t } from 'i18next'
import { type FieldValues, useFormContext } from 'react-hook-form'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle, Button } from '@/components/ui'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import type { TVagasSelecionadas } from '@/features/proposta/brasil/types/proposta.type'
import { ProdutoCard } from '@/shared/produto-card'
import type { TProduto, TProdutoStatusEnum } from '@/types/produto'

type TVagaPadraoSelectorProps = {
  vagas: Array<{ nome: string; produtos: TProduto[] }>
  onConfirm: (selected: TVagasSelecionadas) => void
  handleGoBack?: () => void
}

export const VagaPadraoSelector = ({ vagas, onConfirm, handleGoBack }: TVagaPadraoSelectorProps) => {
  const { watch } = useFormContext<FieldValues>()
  const defaultSelecionadas = watch('informacoesIniciais.vagaIds') as TVagasSelecionadas
  const [vagasSelecionadas, setVagasSelecionadas] = useState<TVagasSelecionadas>(defaultSelecionadas)

  const toggleProduto = (acessorio: TProduto, nomeVaga: string) => {
    setVagasSelecionadas((prev) =>
      prev.some((item) => item.id === acessorio.idExterno)
        ? prev.filter((item) => item.id !== acessorio.idExterno)
        : [...prev, { nomeVaga, id: acessorio.idExterno, descricao: acessorio.descricao }],
    )
  }

  const vagasSelecionadasIds = new Set(vagasSelecionadas.map((vaga) => vaga.id))

  useEffect(() => {
    setVagasSelecionadas(defaultSelecionadas)
  }, [defaultSelecionadas])

  const naoDevePermitirSelecao = (idExterno: number, status: TProdutoStatusEnum) => {
    const vagaNaoEstaSelecionada = !vagasSelecionadasIds.has(idExterno)
    return vagaNaoEstaSelecionada && status !== 'Disponivel'
  }

  return (
    <>
      <SheetHeader
        leftIcon={
          <Render.If condition={!!handleGoBack}>
            {() => (
              <Button
                onClick={handleGoBack}
                variant="ghost"
                className="fit-content text-primary hover:text-primary/70 flex h-8 w-fit p-0 hover:bg-transparent [&_svg]:size-6"
              >
                <Icon name="arrow-left" />
                <p className="hidden text-base font-medium sm:block">{t('common:voltar')}</p>
              </Button>
            )}
          </Render.If>
        }
      >
        <SheetTitle variant="action">{t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.titulo')}</SheetTitle>
        <SheetDescription className="text-sm text-gray-500 sm:text-base">
          {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.descricao')}
        </SheetDescription>
        {!!vagasSelecionadas.length && (
          <p className="mt-2 text-xs text-gray-500 sm:text-sm">
            {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.vagas_selecionadas', {
              quantidade: vagasSelecionadas.length,
            })}
          </p>
        )}
      </SheetHeader>

      <Accordion
        type="multiple"
        className="mb-20 w-full space-y-4 overflow-auto"
        defaultValue={defaultSelecionadas.map((vaga) => vaga.nomeVaga)}
      >
        {vagas.map((vaga) => (
          <AccordionItem value={vaga.nome} key={vaga.nome} className="rounded-md border px-4 py-1 shadow-sm">
            <AccordionTrigger className="py-2 font-medium">
              <div className="capitalize">{vaga.nome.toLocaleLowerCase()}</div>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4 sm:p-1.5">
              {vaga.produtos.map((acessorio) => (
                <ProdutoCard
                  key={acessorio.id}
                  produto={acessorio}
                  nomeAgrupador={vaga.nome}
                  disabled={naoDevePermitirSelecao(acessorio.idExterno, acessorio.status)}
                  isSelected={vagasSelecionadasIds.has(acessorio.idExterno)}
                  className="cursor-pointer disabled:cursor-not-allowed data-[selected=true]:border-2 data-[selected=true]:border-teal-600"
                  handleSelecionarProduto={() => toggleProduto(acessorio, vaga.nome)}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <SheetFooter variant="sticky">
        <Button
          className="w-full justify-self-end aria-disabled:pointer-events-none sm:w-fit"
          onClick={() => onConfirm(vagasSelecionadas)}
        >
          {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.confirmar')}
        </Button>
      </SheetFooter>
    </>
  )
}
